import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileBarChart2, Download, RefreshCw, Loader2, Search, Filter,
  CheckCircle, XCircle, RotateCcw, Clock, AlertTriangle, ArrowLeft,
  ChevronLeft, ChevronRight, Layers, UserCheck, Zap, Hourglass, X,
  FileSpreadsheet, FileText, ChevronDown, ChevronUp, User, Building2, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTION_BADGES = {
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  'Sent Back': 'bg-purple-50 text-purple-700 border-purple-200',
  Forwarded: 'bg-blue-50 text-blue-700 border-blue-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  Upcoming: 'bg-slate-100 text-slate-600 border-slate-200',
  'Not Reached': 'bg-slate-50 text-slate-400 border-slate-200',
};

const STATUS_BADGES = {
  pending: 'bg-amber-100 text-amber-700',
  pending_hr: 'bg-orange-100 text-orange-700',
  in_progress: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  rework: 'bg-purple-100 text-purple-700',
  sent_back: 'bg-purple-100 text-purple-700',
  PAYMENT_PENDING: 'bg-blue-100 text-blue-700',
  paid: 'bg-teal-100 text-teal-700',
};

export default function ConsolidatedManagerApprovalReport() {
  const navigate = useNavigate();

  // Data states
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ managers: [], employees: [], departments: [], companies: [], request_types: [], approval_levels: [] });

  // Filter states
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('all'); // all | completed | pending | overall
  const [approvalLevelFilter, setApprovalLevelFilter] = useState('');
  const [ageingRange, setAgeingRange] = useState(''); // '' | 0-2 | 3-7 | 8-14 | 15+
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Detail Modal state
  const [selectedRow, setSelectedRow] = useState(null);

  // Fetch Report Data
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        state: stateFilter,
      };
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (managerFilter) params.manager = managerFilter;
      if (employeeFilter) params.employee = employeeFilter;
      if (requestTypeFilter) params.request_type = requestTypeFilter;
      if (departmentFilter) params.department = departmentFilter;
      if (companyFilter) params.company_id = companyFilter;
      if (actionFilter) params.action = actionFilter;
      if (statusFilter) params.status = statusFilter;
      if (approvalLevelFilter) params.approval_level = approvalLevelFilter;
      if (ageingRange) params.ageing_range = ageingRange;

      const res = await axios.get(`${API_URL}/manager-approval-report/data`, { params, withCredentials: true });
      setReportData(res.data);
      if (res.data.filter_options) {
        setFilterOptions(res.data.filter_options);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load manager approval report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [
    page, pageSize, stateFilter, search, dateFrom, dateTo,
    managerFilter, employeeFilter, requestTypeFilter, departmentFilter,
    companyFilter, actionFilter, statusFilter, approvalLevelFilter, ageingRange
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setManagerFilter('');
    setEmployeeFilter('');
    setRequestTypeFilter('');
    setDepartmentFilter('');
    setCompanyFilter('');
    setActionFilter('');
    setStatusFilter('');
    setStateFilter('all');
    setApprovalLevelFilter('');
    setAgeingRange('');
    setPage(1);
  };

  const handleExport = (fmt) => {
    const params = new URLSearchParams();
    params.set('format', fmt);
    params.set('state', stateFilter);
    if (search.trim()) params.set('search', search.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (managerFilter) params.set('manager', managerFilter);
    if (employeeFilter) params.set('employee', employeeFilter);
    if (requestTypeFilter) params.set('request_type', requestTypeFilter);
    if (departmentFilter) params.set('department', departmentFilter);
    if (companyFilter) params.set('company_id', companyFilter);
    if (actionFilter) params.set('action', actionFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (approvalLevelFilter) params.set('approval_level', approvalLevelFilter);
    if (ageingRange) params.set('ageing_range', ageingRange);

    window.open(`${API_URL}/manager-approval-report/export?${params.toString()}`, '_blank');
  };

  const summary = reportData?.summary || {
    total_assigned: 0,
    total_actions_completed: 0,
    approved: 0,
    rejected: 0,
    sent_back: 0,
    pending_count: 0,
    average_action_time: 'Unavailable',
    fastest_action_time: 'Unavailable',
    longest_action_time: 'Unavailable',
    overdue_pending_requests: 0,
  };

  const rows = reportData?.rows || [];
  const totalCount = reportData?.total_count || 0;
  const totalPages = reportData?.total_pages || 1;

  const hasActiveFilters = Boolean(
    search || dateFrom || dateTo || managerFilter || employeeFilter ||
    requestTypeFilter || departmentFilter || companyFilter || actionFilter ||
    statusFilter || stateFilter !== 'all' || ageingRange
  );

  return (
    <MainLayout>
      <div data-testid="consolidated-manager-approval-report-page" className="space-y-5">
        {/* Top Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manager/approvals')}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition"
              title="Back to Manager Approvals"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <FileBarChart2 className="w-5 h-5 text-[#0052FF]" /> Consolidated Manager Approval Report
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Consolidated audit report of manager actions, approval turnaround times, and pending stages across your scope.
              </p>
            </div>
          </div>

          {/* Export & Actions Toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="export-csv-btn"
              onClick={() => handleExport('csv')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 transition shadow-xs"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              data-testid="export-xlsx-btn"
              onClick={() => handleExport('xlsx')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 transition shadow-xs"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
            </button>
            <button
              data-testid="export-pdf-btn"
              onClick={() => handleExport('pdf')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition shadow-xs"
            >
              <FileText className="w-3.5 h-3.5" /> PDF
            </button>
            <button
              data-testid="refresh-report-btn"
              onClick={fetchReport}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
              title="Refresh Report"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Section 3: Manager-wise Performance Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="performance-summary-cards">
          {/* Card 1: Total Requests Assigned */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Total Assigned</span>
              <Layers className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-xl font-bold text-slate-900 mt-1" data-testid="summary-total-assigned">
              {summary.total_assigned}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Assigned to manager scope</p>
          </div>

          {/* Card 2: Total Actions Completed */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Actions Completed</span>
              <UserCheck className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xl font-bold text-blue-600 mt-1" data-testid="summary-actions-completed">
              {summary.total_actions_completed}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Approved, rejected, sent back</p>
          </div>

          {/* Card 3: Approved */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Approved</span>
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-xl font-bold text-emerald-700 mt-1" data-testid="summary-approved">
              {summary.approved}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Granted requests</p>
          </div>

          {/* Card 4: Rejected */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Rejected</span>
              <XCircle className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-xl font-bold text-red-600 mt-1" data-testid="summary-rejected">
              {summary.rejected}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Disallowed by manager</p>
          </div>

          {/* Card 5: Sent Back */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Sent Back</span>
              <RotateCcw className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-xl font-bold text-purple-700 mt-1" data-testid="summary-sent-back">
              {summary.sent_back}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Returned for revision</p>
          </div>

          {/* Card 6: Currently Pending */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Currently Pending</span>
              <Clock className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-xl font-bold text-amber-700 mt-1" data-testid="summary-pending">
              {summary.pending_count}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Awaiting manager action</p>
          </div>

          {/* Card 7: Average Action Time */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Avg Action Time</span>
              <Zap className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xl font-bold text-slate-900 mt-1" data-testid="summary-avg-time">
              {summary.average_action_time}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Action Time − Assignment Time</p>
          </div>

          {/* Card 8: Fastest Action Time */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Fastest Action</span>
              <Zap className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-xl font-bold text-emerald-700 mt-1" data-testid="summary-fastest-time">
              {summary.fastest_action_time}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Shortest turnaround</p>
          </div>

          {/* Card 9: Longest Action Time */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Longest Action</span>
              <Hourglass className="w-4 h-4 text-orange-500" />
            </div>
            <p className="text-xl font-bold text-orange-600 mt-1" data-testid="summary-longest-time">
              {summary.longest_action_time}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Maximum turnaround</p>
          </div>

          {/* Card 10: Overdue Pending Requests */}
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[11px] font-semibold uppercase tracking-wider">Overdue (&gt;3d)</span>
              <AlertTriangle className={`w-4 h-4 ${summary.overdue_pending_requests > 0 ? 'text-red-500' : 'text-slate-400'}`} />
            </div>
            <p className={`text-xl font-bold mt-1 ${summary.overdue_pending_requests > 0 ? 'text-red-600' : 'text-slate-800'}`} data-testid="summary-overdue">
              {summary.overdue_pending_requests}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Pending ageing &gt; 3 days</p>
          </div>
        </div>

        {/* Section 4: Filter and Search Panel */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3 shadow-xs">
          {/* Main search and quick state chips */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative w-full">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by req #, employee name/code, manager name..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#0052FF]"
                  data-testid="report-search-input"
                />
              </div>
            </div>

            {/* Quick State Chips: All | Completed | Pending | Overall */}
            <div className="inline-flex bg-slate-100 rounded-lg p-1">
              {[
                { key: 'all', label: 'All Records' },
                { key: 'completed', label: 'Completed Actions' },
                { key: 'pending', label: 'Pending Only' },
                { key: 'overall', label: 'Overall / All Levels' },
              ].map((st) => (
                <button
                  key={st.key}
                  data-testid={`state-filter-${st.key}`}
                  onClick={() => { setStateFilter(st.key); setPage(1); }}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                    stateFilter === st.key ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {st.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              data-testid="toggle-advanced-filters"
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition ${
                showAdvanced || hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-[#0052FF]'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>Filters</span>
              {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-[#0052FF]" />}
              {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Advanced Filters Grid */}
          {showAdvanced && (
            <div className="pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 animate-in fade-in duration-150">
              {/* Approval Level */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Approval Level</label>
                <select
                  data-testid="filter-approval-level"
                  value={approvalLevelFilter}
                  onChange={(e) => { setApprovalLevelFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">All Levels</option>
                  {(filterOptions.approval_levels || ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5']).map((lvl, idx) => (
                    <option key={idx} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>

              {/* Manager */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Manager</label>
                <select
                  data-testid="filter-manager"
                  value={managerFilter}
                  onChange={(e) => { setManagerFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">All Managers</option>
                  {(filterOptions.managers || []).map((m, idx) => (
                    <option key={idx} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* Request Type */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Request Type</label>
                <select
                  data-testid="filter-request-type"
                  value={requestTypeFilter}
                  onChange={(e) => { setRequestTypeFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">All Types</option>
                  {(filterOptions.request_types || []).map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Action Taken */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Action Taken</label>
                <select
                  data-testid="filter-action"
                  value={actionFilter}
                  onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">All Actions</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="sent_back">Sent Back</option>
                  <option value="forwarded">Forwarded</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              {/* Department */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Department</label>
                <select
                  data-testid="filter-department"
                  value={departmentFilter}
                  onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">All Departments</option>
                  {(filterOptions.departments || []).map((d, idx) => (
                    <option key={idx} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Company / Branch */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Company / Branch</label>
                <select
                  data-testid="filter-company"
                  value={companyFilter}
                  onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">All Companies / Branches</option>
                  {(filterOptions.companies || []).map((c, idx) => (
                    <option key={idx} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Ageing Range */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Pending Ageing</label>
                <select
                  data-testid="filter-ageing"
                  value={ageingRange}
                  onChange={(e) => { setAgeingRange(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="">Any Ageing</option>
                  <option value="0-2">0 – 2 days</option>
                  <option value="3-7">3 – 7 days</option>
                  <option value="8-14">8 – 14 days</option>
                  <option value="15+">15+ days</option>
                </select>
              </div>

              {/* Date From */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Date From</label>
                <input
                  type="date"
                  data-testid="filter-date-from"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Date To */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-500">Date To</label>
                <input
                  type="date"
                  data-testid="filter-date-to"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Reset Action */}
              <div className="col-span-full flex justify-end gap-2 pt-1 border-t border-slate-100">
                <button
                  data-testid="reset-filters-btn"
                  onClick={resetFilters}
                  className="text-xs text-slate-500 hover:text-slate-800 underline font-medium"
                >
                  Reset All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Detailed Consolidated Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading approval records…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-slate-400 space-y-2" data-testid="report-no-data">
              <FileBarChart2 className="w-8 h-8 mx-auto text-slate-300" />
              <p className="text-sm font-medium">No approval records found</p>
              <p className="text-xs text-slate-400">Try adjusting your filters or date range.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="consolidated-report-table">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50/70 whitespace-nowrap">
                    <th className="px-3.5 py-3">Manager</th>
                    <th className="px-3.5 py-3">Requester / Employee</th>
                    <th className="px-3.5 py-3">Req #</th>
                    <th className="px-3.5 py-3">Type</th>
                    <th className="px-3.5 py-3">Approval Level</th>
                    <th className="px-3.5 py-3">Department</th>
                    <th className="px-3.5 py-3">Company / Branch</th>
                    <th className="px-3.5 py-3">Submitted At</th>
                    <th className="px-3.5 py-3">Assigned At</th>
                    <th className="px-3.5 py-3">Action Date</th>
                    <th className="px-3.5 py-3">Action Taken</th>
                    <th className="px-3.5 py-3">Time Taken to Act</th>
                    <th className="px-3.5 py-3">Current Status</th>
                    <th className="px-3.5 py-3">Pending With</th>
                    <th className="px-3.5 py-3">Stages</th>
                    <th className="px-3.5 py-3">Remarks</th>
                    <th className="px-3.5 py-3">Pending Ageing</th>
                    <th className="px-3.5 py-3 text-right">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {rows.map((row, idx) => (
                    <tr
                      key={row.id ? `${row.id}-${row.action_at || idx}` : idx}
                      data-testid={`report-row-${idx}`}
                      className="hover:bg-slate-50/80 transition cursor-pointer"
                      onClick={() => setSelectedRow(row)}
                    >
                      {/* Manager Name & Code */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <p className="font-semibold text-slate-900">{row.manager_name || '—'}</p>
                        {row.manager_code && <p className="text-[10px] text-slate-400 font-mono">{row.manager_code}</p>}
                      </td>

                      {/* Requester / Employee */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <p className="font-medium text-slate-800">{row.employee_name || '—'}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{row.employee_code}</p>
                      </td>

                      {/* Request Number */}
                      <td className="px-3.5 py-3 font-mono font-medium text-slate-800 whitespace-nowrap">
                        {row.request_number || row.id?.slice(-6) || '—'}
                      </td>

                      {/* Request Type */}
                      <td className="px-3.5 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {row.request_type_label || row.approval_label || row.approval_type}
                      </td>

                      {/* Approval Level */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded text-[11px] bg-slate-100 text-slate-700 border border-slate-200 font-medium">
                          {row.approval_level || row.stage || 'Level 1'}
                        </span>
                      </td>

                      {/* Department */}
                      <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap">
                        {row.department || '—'}
                      </td>

                      {/* Company / Branch */}
                      <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap">
                        <p>{row.company_name || '—'}</p>
                        {row.branch && <p className="text-[10px] text-slate-400">{row.branch}</p>}
                      </td>

                      {/* Request Submitted Date/Time */}
                      <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap text-[11px]">
                        {row.submitted_at ? row.submitted_at.slice(0, 16).replace('T', ' ') : '—'}
                      </td>

                      {/* Approval Assigned Date/Time */}
                      <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap text-[11px]">
                        {row.assigned_at ? (
                          row.assigned_at.slice(0, 16).replace('T', ' ')
                        ) : (
                          <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200" title="Assignment timestamp not recorded in historical record">
                            Not recorded
                          </span>
                        )}
                      </td>

                      {/* Action Taken Date/Time */}
                      <td className="px-3.5 py-3 text-slate-700 whitespace-nowrap text-[11px]">
                        {row.action_at ? row.action_at.slice(0, 16).replace('T', ' ') : '—'}
                      </td>

                      {/* Action Taken */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${ACTION_BADGES[row.action_taken] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                          {row.action_taken}
                        </span>
                      </td>

                      {/* Time Taken to Act */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 font-medium ${row.action_duration_formatted === 'Unavailable' ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                          <Clock className="w-3 h-3 text-slate-400" />
                          {row.action_duration_formatted || row.time_taken || 'Unavailable'}
                        </span>
                      </td>

                      {/* Current Status */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_BADGES[row.current_status] || 'bg-slate-100 text-slate-600'}`}>
                          {row.display_status || row.current_status}
                        </span>
                      </td>

                      {/* Pending With */}
                      <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap text-[11px]">
                        {row.pending_with || '—'}
                      </td>

                      {/* Total Stages */}
                      <td className="px-3.5 py-3 text-slate-600 text-center whitespace-nowrap">
                        {row.stages_count || 1}
                      </td>

                      {/* Remarks */}
                      <td className="px-3.5 py-3 text-slate-600 max-w-[120px] truncate" title={row.remarks}>
                        {row.remarks || '—'}
                      </td>

                      {/* Pending Ageing */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        {row.is_pending ? (
                          <span className={`inline-flex items-center gap-1 font-semibold ${row.pending_ageing_seconds >= 259200 ? 'text-red-600' : 'text-amber-700'}`}>
                            <Clock className="w-3 h-3" />
                            {row.pending_ageing}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>

                      {/* Action */}
                      <td className="px-3.5 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`row-view-details-${idx}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedRow(row); }}
                          className="px-2.5 py-1 rounded text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 transition"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Footer */}
          <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
            <div>
              Showing <span className="font-semibold">{rows.length}</span> of <span className="font-semibold">{totalCount}</span> records
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span>Per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 border border-slate-200 rounded bg-white text-xs"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  data-testid="pagination-prev"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="p-1.5 rounded border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-100"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="px-2 font-medium">Page {page} of {totalPages}</span>
                <button
                  data-testid="pagination-next"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="p-1.5 rounded border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-100"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Record Detail Modal */}
        {selectedRow && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200"
            onClick={() => setSelectedRow(null)}
            data-testid="approval-record-detail-modal"
          >
            <div
              className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-200 space-y-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    {selectedRow.request_type_label || selectedRow.approval_label} Audit Breakdown
                  </h3>
                  <p className="text-xs text-slate-500 font-mono">Request #{selectedRow.request_number || selectedRow.id}</p>
                </div>
                <button
                  onClick={() => setSelectedRow(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Timing Audit Summary Card */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Timing Audit Distinction</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500 block">Submitted At:</span>
                    <span className="font-semibold text-slate-800">
                      {selectedRow.submitted_at ? selectedRow.submitted_at.replace('T', ' ').slice(0, 19) : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Assigned to Manager At:</span>
                    <span className="font-semibold text-slate-800">
                      {selectedRow.assigned_at ? selectedRow.assigned_at.replace('T', ' ').slice(0, 19) : (
                        <span className="text-amber-600 font-normal">Not recorded (historical)</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Manager Action At:</span>
                    <span className="font-semibold text-slate-800">
                      {selectedRow.action_at ? selectedRow.action_at.replace('T', ' ').slice(0, 19) : 'Pending Action'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Final Completion At:</span>
                    <span className="font-semibold text-slate-800">
                      {selectedRow.completed_at ? selectedRow.completed_at.replace('T', ' ').slice(0, 19) : (selectedRow.is_pending ? 'In Progress' : '—')}
                    </span>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200 flex justify-between items-center text-xs">
                  <span className="text-slate-600 font-medium">Turnaround Duration:</span>
                  <span className="font-bold text-slate-900">
                    {selectedRow.is_pending ? (
                      <span className="text-amber-700">Pending Ageing: {selectedRow.pending_ageing}</span>
                    ) : (
                      <span className="text-[#0052FF]">Time Taken to Act: {selectedRow.time_taken}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Requester & Manager Details */}
              <div className="space-y-2 text-xs">
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Employee</span>
                  <span className="font-semibold text-slate-800">{selectedRow.employee_name} ({selectedRow.employee_code})</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Manager / Approver</span>
                  <span className="font-semibold text-slate-800">{selectedRow.manager_name} {selectedRow.manager_code ? `(${selectedRow.manager_code})` : ''}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Department</span>
                  <span className="text-slate-800">{selectedRow.department || '—'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Company / Branch</span>
                  <span className="text-slate-800">{selectedRow.company_name || '—'} {selectedRow.branch ? `· ${selectedRow.branch}` : ''}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Action Taken</span>
                  <span className={`px-2 py-0.5 rounded font-semibold border ${ACTION_BADGES[selectedRow.action_taken] || 'bg-slate-100 text-slate-700'}`}>
                    {selectedRow.action_taken}
                  </span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Approval Level</span>
                  <span className="font-semibold text-slate-800">{selectedRow.approval_level || selectedRow.stage || 'Level 1'} (Stage {selectedRow.level_index || 1} of {selectedRow.stages_count || 1})</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Workflow Stage Name</span>
                  <span className="text-slate-800">{selectedRow.stage || '—'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Pending With / Next Stage</span>
                  <span className="text-slate-800">{selectedRow.pending_with || selectedRow.next_approver || 'Completed'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-500">Overall Request Status</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_BADGES[selectedRow.current_status] || 'bg-slate-100 text-slate-600'}`}>
                    {selectedRow.final_status || selectedRow.display_status || selectedRow.current_status}
                  </span>
                </div>
                <div className="py-1.5">
                  <span className="text-slate-500 block mb-1">Manager Remarks:</span>
                  <p className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-slate-700 whitespace-pre-wrap">
                    {selectedRow.remarks || 'No remarks entered'}
                  </p>
                </div>
              </div>

              {selectedRow.missing_audit && selectedRow.missing_audit.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Audit Detail Notice</p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      {selectedRow.missing_audit.join('; ')}. Historical record displayed using available database information without fabricated timestamps.
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setSelectedRow(null)}
                  className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
